import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import LiveStrip from "../components/LiveStrip";
import ReviewsBar from "../components/ReviewsBar";
import "../styles/parkingVsTaxi.css";

export default function TrainVsTaxiHeathrowExpress() {
  const DEFAULT_PICKUP = "W11 4JG";
  const TERMINALS = [
    { label: "Heathrow T2 (TW6 1EW)", postcode: "TW6 1EW" },
    { label: "Heathrow T3 (TW6 1QG)", postcode: "TW6 1QG" },
    { label: "Heathrow T4 (TW6 3XA)", postcode: "TW6 3XA" },
    { label: "Heathrow T5 (TW6 2GA)", postcode: "TW6 2GA" }
  ];

  const [form, setForm] = useState({
    pickup_postcode: "",
    destination_postcode: "",
    passengers: 2,
    luggage: 2,
    pickup_datetime: ""
  });
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [pickupWarning, setPickupWarning] = useState("");
  const [pickupFocused, setPickupFocused] = useState(false);
  const pickupTimer = useRef(null);

  const [loading, setLoading] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState([]);
  const [loadingSlow, setLoadingSlow] = useState(false);
  const loadingTimers = useRef([]);

  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const autoSubmitted = useRef(false);

  useEffect(() => {
    const nextMondayAt11 = () => {
      const now = new Date();
      const day = now.getDay();
      const daysToMon = (8 - day) % 7 || 7;
      const target = new Date(now);
      target.setDate(now.getDate() + daysToMon);
      target.setHours(11, 0, 0, 0);
      const pad = n => String(n).padStart(2, "0");
      return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
    };

    setForm(prev => ({
      ...prev,
      pickup_postcode: DEFAULT_PICKUP,
      destination_postcode: TERMINALS[1].postcode,
      passengers: 3,
      luggage: 2,
      pickup_datetime: nextMondayAt11()
    }));
  }, []);

  useEffect(() => {
    if (autoSubmitted.current) return;
    if (form.pickup_postcode && form.destination_postcode && form.pickup_datetime) {
      autoSubmitted.current = true;
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickup_postcode, form.destination_postcode, form.pickup_datetime]);

  const isLikelyUkPostcode = (value) => {
    if (!value) return false;
    const norm = String(value).toUpperCase().trim();
    const pcRegex = /^([A-Z]{1,2}[0-9][0-9A-Z]?|[A-Z][0-9][A-Z]|[A-Z]{2}[0-9]{2})\s*[0-9][A-Z]{2}$/;
    return pcRegex.test(norm);
  };

  const fetchPickupSuggestions = (value) => {
    const v = String(value || "");
    if (pickupTimer.current) clearTimeout(pickupTimer.current);
    if (v.length < 2) {
      setPickupSuggestions([]);
      setPickupWarning("");
      return;
    }
    if (v.length >= 4 && !isLikelyUkPostcode(v)) {
      setPickupWarning("Please enter a valid UK postcode (e.g. W11 4JG).");
    } else {
      setPickupWarning("");
    }
    pickupTimer.current = setTimeout(async () => {
      try {
        const resp = await api.get("/api/postcodes/suggest", { params: { q: v, limit: 10 } });
        const suggestions = resp.data?.suggestions || [];
        setPickupSuggestions(
          suggestions.map((pc) => ({
            postcode: pc,
            label: "Suggested",
            code: null,
          }))
        );
      } catch (e) {
        setPickupSuggestions([]);
      }
    }, 180);
  };

  const resetLoadingIndicators = () => {
    loadingTimers.current.forEach((t) => clearTimeout(t));
    loadingTimers.current = [];
    setLoadingSteps([]);
    setLoadingSlow(false);
  };

  const startLoadingIndicators = () => {
    resetLoadingIndicators();
    const steps = [
      { id: "distance", label: "Checking distance & time", status: "done" },
      { id: "taxi", label: "Checking taxi & ride-hail", status: "pending" },
      { id: "rail", label: "Adding Heathrow Express fares", status: "pending" },
      { id: "compare", label: "Comparing cost & hassle", status: "pending" },
    ];
    setLoadingSteps(steps);
    loadingTimers.current.push(
      setTimeout(() => setLoadingSteps((prev) => prev.map((s) => s.id === "taxi" ? { ...s, status: "done" } : s)), 500),
      setTimeout(() => setLoadingSteps((prev) => prev.map((s) => s.id === "rail" ? { ...s, status: "done" } : s)), 900),
      setTimeout(() => setLoadingSteps((prev) => prev.map((s) => s.id === "compare" ? { ...s, status: "done" } : s)), 1300),
      setTimeout(() => setLoadingSlow(true), 1600)
    );
  };

  const submit = async () => {
    if (pickupWarning || !isLikelyUkPostcode(form.pickup_postcode)) {
      setPickupWarning("Please enter a valid UK postcode (e.g. W11 4JG).");
      setError("Please enter a valid pickup postcode.");
      return;
    }
    setLoading(true);
    setError(null);
    startLoadingIndicators();

    try {
      const payload = {
        ...form,
        pickup_postcode: form.pickup_postcode.replace(/\(.*?\)/g, "").trim(),
        passengers: Math.min(6, Math.max(1, Number(form.passengers) || 1)),
        luggage: Math.min(6, Math.max(0, Number(form.luggage) || 0)),
        destination_locked: true
      };
      const res = await api.post("/api/train/compare", payload);
      setResult(res.data);
    } catch (err) {
      setError("Failed to calculate comparison. Please try again.");
    }

    setLoading(false);
    resetLoadingIndicators();
  };

  const renderMoney = v => (v == null ? "—" : `£${Number(v).toFixed(2)}`);

  const apiData = result?.data || {};
  const options = apiData.options || [];
  const journey = result?.journey || {};
  const providers = apiData.meta?.providers || {};
  const legacyCosts = result?.costs || {};
  const pax = journey?.passengers ?? form.passengers ?? 1;

  const taxiOption = options.find(o => o.id === "taxi_ccg") || null;
  const rideHailRange = providers?.ride_hail?.breakdown?.range || providers?.ride_hail?.range || null;
  const rideHailMid = rideHailRange?.mid ?? providers?.ride_hail?.mid ?? null;
  const rideHailOption =
    options.find(o => o.id === "ride_hail_estimate") ||
    (rideHailMid != null
      ? {
          id: "ride_hail_estimate",
          label: "Ride-hail (estimate)",
          mode: "taxi",
          total_cost_gbp: Number(rideHailMid),
          breakdown: { range: rideHailRange || providers.ride_hail },
        }
      : null);
  const blackCabRange = providers?.black_cab?.breakdown?.range || providers?.black_cab?.range || null;
  const blackCabMid = blackCabRange?.mid ?? providers?.black_cab?.mid ?? null;
  const blackCabOption =
    options.find(o => o.id === "black_cab_estimate") ||
    (blackCabMid != null
      ? {
          id: "black_cab_estimate",
          label: "Black cab (estimate)",
          mode: "taxi",
          total_cost_gbp: Number(blackCabMid),
          breakdown: { range: blackCabRange || providers.black_cab, note: providers.black_cab_note || null },
        }
      : null);
  const hexCost = legacyCosts?.train?.total ?? null;
  const hexDuration = legacyCosts?.train?.duration_minutes ?? journey?.duration_minutes ?? null;
  const hexPerPerson = legacyCosts?.train?.per_person ?? null;
  const hexWalk = journey?.total_walk_minutes ?? null;
  const hexChanges = journey?.interchanges ?? null;
  const heathrowExpressOption =
    hexCost != null
      ? {
          id: "heathrow_express",
          label: "Heathrow Express",
          mode: "train",
          total_cost_gbp: Number(hexCost),
          breakdown: {
            duration_minutes: hexDuration,
            walk_minutes: hexWalk,
            interchanges: hexChanges,
            fare_per_person_gbp: hexPerPerson,
            source: "Heathrow Express fare logic (unchanged)",
          },
        }
      : null;

  const candidateOptions = [taxiOption, rideHailOption, blackCabOption, heathrowExpressOption].filter(Boolean);
  const rankedOptions = candidateOptions
    .filter(o => typeof o.total_cost_gbp === "number" && !Number.isNaN(o.total_cost_gbp))
    .sort((a, b) => a.total_cost_gbp - b.total_cost_gbp);
  const winnerOption = rankedOptions[0] || null;
  const runnerUp = rankedOptions[1] || null;
  const savingVsNext =
    winnerOption && runnerUp ? Number((runnerUp.total_cost_gbp - winnerOption.total_cost_gbp).toFixed(2)) : null;

  const durationFor = (opt) => {
    if (!opt) return null;
    return opt.breakdown?.duration_minutes ?? apiData.meta?.taxi_duration_mins ?? journey?.duration_minutes ?? null;
  };
  const walkFor = (opt) => {
    if (!opt) return null;
    if (opt.mode === "taxi") return 0;
    return opt.breakdown?.walk_minutes ?? journey?.total_walk_minutes ?? 0;
  };
  const changesFor = (opt) => {
    if (!opt) return null;
    if (opt.mode === "taxi") return 0;
    return opt.breakdown?.interchanges ?? 0;
  };
  const hassleFor = (opt) => {
    const walk = walkFor(opt);
    const changes = changesFor(opt);
    if (walk >= 20 || changes >= 2) return { label: "High", color: "#b91c1c", emoji: "🔴" };
    if (walk >= 10 || changes >= 1) return { label: "Medium", color: "#b45309", emoji: "🟠" };
    return { label: "Low", color: "#15803d", emoji: "🟢" };
  };

  const renderHero = () => {
    const medals = ["GOLD", "SILVER", "BRONZE"];
    const badgeColors = {
      GOLD: { bg: "#0f172a", text: "#fbbf24", border: "#22c55e", emoji: "🥇" },
      SILVER: { bg: "#111827", text: "#e5e7eb", border: "#cbd5e1", emoji: "🥈" },
      BRONZE: { bg: "#0f172a", text: "#fb923c", border: "#fb923c", emoji: "🥉" },
    };

    const tiles = medals.map((medal, idx) => {
      const opt = rankedOptions[idx] || null;
      const palette = badgeColors[medal] || badgeColors.GOLD;
      const duration = durationFor(opt);
      const walk = walkFor(opt);
      const hassle = opt ? hassleFor(opt) : null;
      const label = opt?.id === "taxi_ccg" ? "Taxi" : opt?.label || "—";
      const perPerson =
        opt?.id === "heathrow_express" && opt?.breakdown?.fare_per_person_gbp != null
          ? `(${renderMoney(opt.breakdown.fare_per_person_gbp)} pp × ${pax})`
          : null;

      return (
        <div
          key={medal}
          style={{
            border: `1px solid ${palette.border}`,
            borderRadius: 14,
            padding: 14,
            background: palette.bg,
            color: palette.text,
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>
            {palette.emoji} {medal}
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, marginTop: 6, color: "#fff" }}>{label}</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginTop: 4, color: "#fff" }}>
            {opt ? renderMoney(opt.total_cost_gbp) : "—"} {perPerson}
          </div>
          {opt && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6, fontSize: 12, color: "#e5e7eb" }}>
              <span>{duration ? `⏱ ${duration} min` : "⏱ —"}</span>
              <span>{walk ? `🚶 ${walk} min walk` : "🚪 Door-to-terminal"}</span>
              {hassle && <span>{hassle.emoji} {hassle.label} hassle</span>}
            </div>
          )}
        </div>
      );
    });

    return (
      <div
        style={{
          borderRadius: 18,
          padding: 18,
          marginBottom: 16,
          color: "#fff",
          background: "linear-gradient(120deg, #0f172a 0%, #22c55e 100%)",
          boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.4, textTransform: "uppercase", opacity: 0.9 }}>
          Recommendation
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
          {winnerOption ? `✅ Best: ${winnerOption.id === "taxi_ccg" ? "Taxi" : winnerOption.label}` : "No winner yet"}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, opacity: 0.95 }}>
          {winnerOption?.id === "heathrow_express"
            ? "Fastest rail to the terminals. Paddington access still matters."
            : "Taxi and ride-hail include drop-off fee and are door-to-terminal."}
        </div>
        <div style={{ fontSize: 14, marginTop: 6, opacity: 0.95 }}>
          {Number.isFinite(savingVsNext)
            ? `~£${savingVsNext} cheaper than the next best option`
            : "Sorted by total one-way cost."}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 10, fontSize: 12, fontWeight: 700, flexWrap: "wrap" }}>
          <span>💷 Cheapest</span>
          <span>🧳 Luggage-friendly</span>
          <span>🚪 Door-to-terminal</span>
        </div>
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
          }}
        >
          {tiles}
        </div>
      </div>
    );
  };

  const renderFareCards = () => {
    const accessNote = (() => {
      const dest = (form.destination_postcode || "").toUpperCase().replace(/\s+/g, "");
      if (dest === "TW63XA") return "T4 needs a change at Heathrow Central + shuttle (adds ~15–20 min).";
      if (dest === "TW62GA") return "T5 station is in-terminal; follow lifts/escalators to departures.";
      return "T2/T3 share Heathrow Central; allow 5–10 min walk via the subway.";
    })();

    const optionRow = (title, opt, extra) => {
      if (!opt) return null;
      const range = opt.breakdown?.range;
      const note = opt.breakdown?.note;
      return (
        <div className="train-fare-row">
          <div className="train-fare-label">{title}</div>
          <div className="train-fare-value strong">
            {range
              ? `${renderMoney(range.min)} – ${renderMoney(range.max)}`
              : renderMoney(opt.total_cost_gbp)}
            {extra && <div className="train-fare-sub">{extra}</div>}
            {note && <div className="train-fare-sub">{note}</div>}
          </div>
        </div>
      );
    };

    const perPerson =
      heathrowExpressOption?.breakdown?.fare_per_person_gbp != null
        ? `(${renderMoney(heathrowExpressOption.breakdown.fare_per_person_gbp)} per person × ${pax})`
        : null;

    return (
      <div className="train-fare-grid">
        <div className="train-fare-group">
          <div className="train-fare-group-heading">🚆 Heathrow Express</div>
          {heathrowExpressOption ? (
            <>
              <div className="train-fare-row">
                <div className="train-fare-label">Heathrow Express (rail only)</div>
                <div className="train-fare-value">
                  {renderMoney(heathrowExpressOption.total_cost_gbp)}
                  {perPerson && <div className="train-fare-sub">{perPerson}</div>}
                  <div className="train-fare-sub">Includes Heathrow Central ↔ terminal walk/shuttle where needed.</div>
                </div>
              </div>
            </>
          ) : (
            <div className="train-fare-row">
              <div className="train-fare-label">Heathrow Express</div>
              <div className="train-fare-value">—</div>
            </div>
          )}
        </div>

        <div className="train-fare-group">
          <div className="train-fare-group-heading">🚖 Taxi & Ride-hail</div>
          {optionRow("CheapCabGrab (fixed)", taxiOption)}
          {optionRow("Ride-hail (estimate)", rideHailOption)}
          {optionRow("Black cab (estimate)", blackCabOption)}
          <div className="train-fare-row muted-row">
            <div className="train-fare-label">Access + terminal walk</div>
            <div className="train-fare-value muted">{accessNote}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderLoading = () => {
    if (!loading && !loadingSteps.length) return null;
    return (
      <div className="parking-results" style={{ marginTop: 12 }}>
        <div className="train-summary-bar" style={{ background: "#0f172a", color: "#fff" }}>
          Crunching Heathrow Express vs taxi…
        </div>
        <div style={{ padding: 12, background: "#fff", border: "1px solid #e5e7eb", borderTop: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {loadingSteps.map((s) => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <span>{s.status === "done" ? "✅" : "⏳"}</span>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
          {loadingSlow && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#b45309" }}>
              Live fares can take a few seconds — thanks for waiting.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>Heathrow Express vs Taxi (2026): Which Is Cheaper?</title>
        <meta
          name="description"
          content="Compare Heathrow Express vs taxi vs ride-hail for Heathrow terminals. Live taxi preview pricing, existing Heathrow Express fares, hassle and walking time."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/heathrow-express-vs-taxi" />
        <meta property="og:title" content="Heathrow Express vs Taxi (2026)" />
        <meta
          property="og:description"
          content="See Heathrow Express fares against taxi and ride-hail with luggage, walking and terminal access included."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/heathrow-express-vs-taxi" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Heathrow Express vs Taxi",
            url: "https://www.cheapcabgrab.com/heathrow-express-vs-taxi",
            description: "Compare Heathrow Express fares with taxi and ride-hail to Heathrow terminals.",
            inLanguage: "en-GB",
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Is Heathrow Express cheaper than a taxi?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Often for solo travellers. For 3–4 people, per-person rail fares can exceed a fixed-price taxi.",
                },
              },
              {
                "@type": "Question",
                name: "Does this include terminal walks and shuttles?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. We show Heathrow Central walks for T2/3 and the T4 shuttle so you know the true door-to-terminal effort.",
                },
              },
              {
                "@type": "Question",
                name: "Are taxi prices live?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "CheapCabGrab uses live preview pricing. Ride-hail and black cab are modelled estimates.",
                },
              },
              {
                "@type": "Question",
                name: "Which terminals are supported?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "T2, T3, T4 and T5. Destination is locked to Heathrow to keep comparisons accurate.",
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <div className="parking-header">
        <h1>Heathrow Express vs Taxi (2026)</h1>
        <p style={{ fontWeight: 700, color: "#111827", marginBottom: 6 }}>
          Compare Heathrow Express against taxi and ride-hail with terminal walking baked in.
        </p>
        <div style={{ fontSize: 12.5, color: "#475467", marginTop: 4, lineHeight: 1.6 }}>
          <div>Did you know? Heathrow Express tickets spike on same-day/short-notice fares. This comparison uses January 2026 TfL/Heathrow pricing so you see the true airport cost before you travel.</div>
          <div style={{ marginTop: 4 }}>Paddington gap: Heathrow Express ends at Paddington, which can be ~3.2 miles from Soho/Mayfair/South Ken. Expect ~£25 for a black cab from Paddington or carry bags onto the Bakerloo/Elizabeth lines.</div>
        </div>
        <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 999, fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
          Updated 1 Feb 2026 • Includes access + terminal walk notes
        </div>
      </div>

      <div className="parking-form">
        <div className="form-group">
          <label>Pickup postcode</label>
          <input
            className="parking-input"
            value={form.pickup_postcode}
            placeholder="e.g. W11 4JG"
            onChange={(e) => {
              setForm({ ...form, pickup_postcode: e.target.value });
              fetchPickupSuggestions(e.target.value);
            }}
            onFocus={() => setPickupFocused(true)}
            onBlur={() => setTimeout(() => setPickupFocused(false), 120)}
          />
          {pickupWarning && <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}>{pickupWarning}</div>}
          {pickupFocused && pickupSuggestions.length > 0 && (
            <div className="suggestion-box">
              {pickupSuggestions.map((s, idx) => (
                <div
                  key={`${s.postcode}-${idx}`}
                  className="suggestion-item"
                  onClick={() => {
                    setForm({ ...form, pickup_postcode: s.postcode });
                    setPickupFocused(false);
                    setPickupSuggestions([]);
                  }}
                >
                  <div className="suggestion-main">{s.postcode}</div>
                  <div className="suggestion-sub">{s.label || "Suggested"}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Heathrow terminal</label>
          <select
            className="parking-input"
            value={form.destination_postcode}
            onChange={(e) => setForm({ ...form, destination_postcode: e.target.value })}
          >
            {TERMINALS.map((t) => (
              <option key={t.postcode} value={t.postcode}>{t.label}</option>
            ))}
          </select>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
            Destination locked: Heathrow only (T2–T5)
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
          <div className="form-group">
            <label>Passengers</label>
            <input
              type="number"
              className="parking-input"
              min="1"
              max="6"
              step="1"
              value={form.passengers}
              onChange={(e) => setForm({ ...form, passengers: e.target.value === "" ? "" : Math.min(6, Math.max(1, Number(e.target.value))) })}
              onBlur={() => {
                const num = Number(form.passengers);
                setForm({ ...form, passengers: Number.isNaN(num) ? 1 : Math.min(6, Math.max(1, num)) });
              }}
            />
          </div>

          <div className="form-group">
            <label>Luggage</label>
            <input
              type="number"
              className="parking-input"
              min="0"
              max="6"
              step="1"
              value={form.luggage}
              onChange={(e) => setForm({ ...form, luggage: e.target.value === "" ? "" : Math.min(6, Math.max(0, Number(e.target.value))) })}
              onBlur={() => {
                const num = Number(form.luggage);
                setForm({ ...form, luggage: Number.isNaN(num) ? 0 : Math.min(6, Math.max(0, num)) });
              }}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Pickup date & time</label>
          <input
            type="datetime-local"
            className="parking-input"
            value={form.pickup_datetime}
            onChange={(e) => setForm({ ...form, pickup_datetime: e.target.value })}
          />
        </div>

        <button onClick={submit} className="parking-btn">
          {loading ? "Comparing…" : "Compare Heathrow Express vs Taxi"}
        </button>
        {error && <p style={{ color: "red", marginTop: "10px" }}>{error}</p>}
      </div>

      {renderLoading()}

      {result && rankedOptions.length > 0 && (
        <div className="parking-results">
          {renderHero()}

          <details className="train-collapsible" open>
            <summary className="train-summary-bar">💷 Fares (one way)</summary>
            <div className="train-collapsible-body">{renderFareCards()}</div>
          </details>

          <details className="train-collapsible" open>
            <summary className="train-summary-bar">😌 Hassle & walking</summary>
            <div className="train-collapsible-body">
              <div style={{ fontSize: 13, color: "#475467", marginBottom: 8 }}>
                Includes terminal walks and shuttle where relevant. Taxi is door-to-terminal.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 10 }}>
                {candidateOptions.map((opt) => {
                  const hassle = hassleFor(opt);
                  const walk = walkFor(opt);
                  const changes = changesFor(opt);
                  return (
                    <div key={opt.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, background: "#fff" }}>
                      <div style={{ fontWeight: 800 }}>{opt.id === "taxi_ccg" ? "Taxi" : opt.label}</div>
                      <div style={{ fontSize: 13, marginTop: 4 }}>{hassle.emoji} {hassle.label} hassle</div>
                      <div style={{ fontSize: 12, color: "#475467", marginTop: 6 }}>
                        {walk ? `🚶 ${walk} min walk` : "🚪 Door-to-terminal"} • {changes ? `${changes} change` : "0 changes"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </details>

          <details className="train-collapsible">
            <summary className="train-summary-bar">ℹ️ Transparency</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>CheapCabGrab uses live preview pricing (one way, includes Heathrow drop-off).</li>
                <li>Ride-hail and black cab are modelled estimates using the same logic as our airport tools.</li>
                <li>Heathrow Express fares use the existing calculator logic (kept unchanged).</li>
                <li>We lock destination to Heathrow to avoid misleading routes.</li>
              </ul>
            </div>
          </details>
        </div>
      )}

      <div style={{ marginTop: 16, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f9fafb" }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>Quick tips</div>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
          <li>Groups of 3–4 often find taxi cheaper than per-person Heathrow Express fares.</li>
          <li>T4 adds a shuttle change; taxi goes direct to arrivals.</li>
          <li>T2/T3 walk via Heathrow Central; allow 5–10 minutes with bags.</li>
        </ul>
      </div>

      <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>Why Heathrow Express often loses for groups</div>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
          <li>Per-person pricing (~£26 same-day single) multiplies for 3–4 people.</li>
          <li>Luggage handling across Paddington platforms, escalators, and Heathrow walks.</li>
          <li>Paddington access adds time/cost before you even board the train.</li>
          <li>T4 requires a change at Heathrow Central (extra wait and walk).</li>
          <li>Missed trains are sunk cost; taxis are flexible and door-to-door.</li>
        </ul>
      </div>

      <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f9fafb" }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>When Heathrow Express can win</div>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
          <li>Solo travellers or pairs with light bags and already near Paddington.</li>
          <li>Advance tickets (£10+ booked weeks ahead) and off-peak travel.</li>
          <li>Peak road traffic or late-night surcharges make taxi slower/costlier.</li>
        </ul>
      </div>

      <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>Heathrow terminal notes</div>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
          <li>T2/T3: Heathrow Express stops at Heathrow Central → ~5–10 minute walk via subway.</li>
          <li>T5: Station is inside the terminal basement (lifts/escalators to check-in).</li>
          <li>T4: Change at Heathrow Central and take the free shuttle; allow extra time.</li>
          <li>Taxi drops at the terminal entrance — no walking or transfers.</li>
        </ul>
      </div>

      <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f8fafc" }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>FAQs</div>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
          <li><strong>Is Heathrow Express cheaper than a taxi?</strong> Often not for groups; the calculator shows the live fare vs a fixed taxi price.</li>
          <li><strong>Is Heathrow Express good for families?</strong> Per-person fares stack up; taxis are usually cheaper for 3–4 people and handle luggage better.</li>
          <li><strong>Do kids really travel free on Heathrow Express?</strong> Under 15s can ride free with an adult, but adult fares still add up for groups.</li>
          <li><strong>Is Heathrow Express worth it with luggage?</strong> Only if you want rail speed and have minimal bags; otherwise taxi removes platform walking.</li>
          <li><strong>Is Heathrow Express faster than a taxi?</strong> Sometimes, but taxi is door-to-door. Check the live duration and hassle scores above.</li>
          <li><strong>Which terminals does Heathrow Express serve?</strong> T2/3 via Heathrow Central, T5 in-terminal; T4 needs a change/shuttle.</li>
          <li><strong>What if my flight is early morning or late night?</strong> Check first/last trains on TfL/Heathrow Express; taxis run 24/7.</li>
        </ul>
      </div>

      <details className="train-collapsible" style={{ marginTop: 12 }}>
        <summary className="train-summary-bar">ℹ️ Reference fares &amp; assumptions</summary>
        <div className="train-collapsible-body">
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
            <li>Heathrow Express: ~£26 same-day single (adult), ~£10 advance (30+ days) — January 2026 pricing.</li>
            <li>Paddington access may add Tube/rail/bus/taxi cost before boarding Heathrow Express.</li>
            <li>Taxi drop-off fee: £7 (Blue Badge can be £0 if registered).</li>
            <li>First/last trains vary by terminal; confirm on TfL before travel.</li>
            <li>Train times and fares subject to change; check TfL website for updates.</li>
          </ul>
        </div>
      </details>

      <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f9fafb" }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>See other relevant calculators</div>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
          <li><a href="/train-vs-taxi">Train vs Taxi (main tool)</a> — includes group travel comparison.</li>
          <li><a href="/train-vs-taxi-for-groups">Train vs Taxi for Groups</a> — prefilled for 4 passengers with luggage.</li>
          <li><a href="/heathrow-parking-vs-taxi">Parking vs Taxi (main)</a> and <a href="/7-day-heathrow-parking-vs-taxi">7-day version</a>.</li>
          <li><a href="/central-london-heathrow-parking-vs-taxi">Central London parking vs taxi</a> and <a href="/oxford-heathrow-parking-vs-taxi">Oxford/Reading variants</a>.</li>
          <li><a href="/heathrow-dropoff-free">Heathrow drop-off guide</a> and <a href="/heathrow-fare-compare">Heathrow fare compare</a>.</li>
          <li><a href="/train-vs-taxi-elizabeth-line">Elizabeth Line vs Taxi</a> and <a href="/train-vs-taxi-for-groups">Group train vs taxi</a>.</li>
          <li><a href="/heathrow-t5-dropoff">Terminal 5 drop-off &amp; pickup</a> and <a href="/heathrow-taxi">Heathrow taxi transfers</a>.</li>
        </ul>
      </div>

      <div style={{ marginTop: "32px" }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
